/**
 * Questo file definisce la pagina di Dettaglio di una Cartella Pubblica.
 * - Mostra le informazioni di una specifica cartella pubblica, come il nome, l'autore
 * e l'elenco delle flashcard contenute.
 * - È una pagina accessibile a tutti gli utenti, anche non autenticati.
 * - Recupera l'ID della cartella dai parametri dell'URL (`useParams`).
 * - Fa il dispatch di un'azione Redux (`fetchPublicFolderDetails`) per caricare i dati
 * della cartella specifica.
 * - Gestisce la paginazione delle flashcard per non sovraccaricare la pagina se ce ne sono molte.
 * - Fornisce un pulsante "Avvia Studio" per passare alla sessione di studio pubblica.
 * - Gestisce gli stati di caricamento e di errore.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
// Importa le azioni Redux necessarie.
import { fetchPublicFolderDetails, clearCurrentPublicFolder } from '../redux/flashcardSlice';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import FlashcardItem from '../components/FlashcardItem';
import './FolderDetailPage.css';

// Costante per il numero di card da mostrare per pagina.
const CARDS_PER_PAGE = 9;

export default function FolderDetailPage() {
    // Ottiene `folderId` dai parametri della rotta (es. /public/folder/:folderId).
    const { folderId } = useParams();
    const dispatch = useDispatch();
    // Estrae dallo store Redux i dati della cartella pubblica corrente, lo stato di caricamento e di errore.
    const { currentPublicFolder, loading, error } = useSelector((state) => state.flashcards);

    // Stato locale per la pagina corrente della paginazione.
    const [currentPage, setCurrentPage] = useState(1);

    // Effetto per caricare i dati della cartella al montaggio o al cambio di `folderId`.
    useEffect(() => {
        if (folderId) {
            dispatch(fetchPublicFolderDetails(folderId));
        }
        // Funzione di pulizia: quando il componente viene smontato, pulisce i dati della
        // cartella corrente dallo store per evitare di mostrare dati vecchi al prossimo accesso.
        return () => {
            dispatch(clearCurrentPublicFolder());
        };
    }, [dispatch, folderId]);

    // `useMemo` per calcolare le flashcard da visualizzare nella pagina corrente.
    // Viene ricalcolato solo se `currentPublicFolder` o `currentPage` cambiano, ottimizzando le performance.
    const paginatedFlashcards = useMemo(() => {
        if (!currentPublicFolder?.flashcards) return [];
        const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
        return currentPublicFolder.flashcards.slice(startIndex, startIndex + CARDS_PER_PAGE);
    }, [currentPublicFolder, currentPage]);

    // Calcola il numero totale di pagine necessarie.
    const totalPages = Math.ceil((currentPublicFolder?.flashcards?.length || 0) / CARDS_PER_PAGE);

    // Gestore per il cambio di pagina.
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // --- Logica di Rendering Condizionale ---

    if (loading) {
        return <LoadingSpinner message="Caricamento flashcard..." />;
    }

    if (error) {
        return (
            <>
                <Navbar />
                <div className="detail-page-container text-center">
                    <div className="alert alert-danger">
                        <h4><i className="bi bi-exclamation-triangle-fill me-2"></i>Errore</h4>
                        <p>{error}</p>
                        <Link to="/public" className="btn btn-primary">Torna all'elenco</Link>
                    </div>
                </div>
            </>
        );
    }

    // Se non c'è una cartella caricata (es. dopo la pulizia), non renderizza nulla.
    if (!currentPublicFolder) {
        return null;
    }

    // --- Renderizzazione della Pagina ---
    return (
        <>
            <Navbar />
            <div className="detail-page-container">
                <div className="container">
                    <div className="detail-page-header">
                        <div>
                            <Link to="/public" className="back-link">
                                <i className="bi bi-arrow-left-circle"></i> Torna all'elenco
                            </Link>
                            <h1 className="display-5 fw-bold mt-3">{currentPublicFolder.nome}</h1>
                            <p className="text-muted">
                                Una collezione di {currentPublicFolder.flashcards.length} flashcard create da <strong>{currentPublicFolder.autore}</strong>.
                            </p>
                        </div>
                        {/* Pulsante per avviare la sessione di studio, disabilitato se non ci sono card. */}
                        <Link
                            to={`/public/study/${folderId}`}
                            className={`btn btn-primary btn-lg study-now-button ${currentPublicFolder.flashcards.length === 0 ? 'disabled' : ''}`}
                        >
                            <i className="bi bi-play-circle-fill me-2"></i>Avvia Studio
                        </Link>
                    </div>

                    {/* Griglia delle flashcard paginata */}
                    <div className="row g-4">
                        {paginatedFlashcards.map(card => (
                            <div className="col-md-6 col-lg-4" key={card.id}>
                                {/* Usa il componente FlashcardItem in modalità vista pubblica */}
                                <FlashcardItem card={card} isPublicView={true} />
                            </div>
                        ))}
                    </div>

                    {/* Controlli di paginazione, mostrati solo se c'è più di una pagina */}
                    {totalPages > 1 && (
                        <nav className="d-flex justify-content-center mt-5">
                            <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Precedente</button>
                                </li>
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Successivo</button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </div>
            </div>
        </>
    );
}